import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { dealService, Deal, DealParty } from '../services/dealService';
import { dealAmount } from '../utils/dealsBilling';
import { getAssetUrl } from '../utils/assets';

const LOGO = '/InvoicesImages/UptimioInvoices.svg';
const ISSUER_SIGNATURE = '/UptimioSignature.png';

const a4Styles: React.CSSProperties = {
  width: '210mm',
  minHeight: '297mm',
  margin: '0 auto',
  background: '#fff',
  padding: '16mm',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column'
};

const money = (value: number) =>
  Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const addressLines = (party?: DealParty | null) => {
  if (!party) return [];
  const cityLine = [party.city, party.state].filter(Boolean).join(', ');
  const zipCountry = [party.zipCode, party.country].filter(Boolean).join(', ');
  return [party.street, cityLine, zipCountry].filter(Boolean);
};

const PartyDetails: React.FC<{ party: DealParty | null; title: string }> = ({ party, title }) => {
  if (!party) return null;
  const lines = addressLines(party);
  return (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 16, fontWeight: 700 }}>{party.companyName}</div>
      {party.contactName && party.contactName !== party.companyName && <div>{party.contactName}</div>}
      {party.email && <div>{party.email}</div>}
      {party.phone && <div>{party.phone}</div>}
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
      {party.taxId && <div>Tax ID: {party.taxId}</div>}
    </div>
  );
};

const DealPdf: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [issuer, setIssuer] = useState<DealParty | null>(null);
  const [client, setClient] = useState<DealParty | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) {
      setError('Missing deal id');
      setLoading(false);
      return;
    }
    dealService.get(id)
      .then((doc) => {
        setDeal(doc.deal);
        setIssuer(doc.issuer);
        setClient(doc.client);
      })
      .catch((err: any) => {
        setError(err.response?.data?.message || 'Could not load deal');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const signedOn = deal
    ? new Date(deal.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="deal-pdf" style={{ padding: 24, background: '#e5e7eb', minHeight: '100vh' }}>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          html, body { margin: 0 !important; background: #fff !important; }
          .no-print { display: none !important; }
          .deal-pdf { padding: 0 !important; background: #fff !important; }
          .deal-paper { width: 100% !important; min-height: 0 !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important; }
        }
        .deal-paper { box-shadow: 0 8px 30px rgba(15, 23, 42, 0.12); }
        .deal-pdf-table { width: 100%; border-collapse: collapse; margin-top: 18px; }
        .deal-pdf-table th, .deal-pdf-table td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
        .deal-pdf-table th { color: #6b7280; font-weight: 600; width: 38%; }
        .deal-signatures { display: flex; justify-content: space-between; gap: 40px; margin-top: auto; padding-top: 56px; }
        .deal-signature { width: 46%; text-align: center; }
        .deal-signature-mark {
          height: 64px;
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }
        .deal-signature-mark img { max-height: 64px; max-width: 100%; object-fit: contain; }
        .deal-signature-line { border-bottom: 1px solid #111827; margin-top: 4px; }
        .deal-signature-role { display: block; margin-top: 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #6b7280; }
        .deal-signature-name { display: block; margin-top: 2px; font-size: 13px; font-weight: 600; color: #111827; }
      `}</style>

      <div className="no-print" style={{ maxWidth: 794, margin: '0 auto 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Deal</h2>
        <Button label="Download PDF" icon="pi pi-download" onClick={() => window.print()} disabled={!deal} />
      </div>

      {loading && <p>Loading…</p>}
      {error && <Message severity="error" text={error} />}

      {deal && (
        <div className="deal-paper" style={a4Styles}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
            <div>
              <img src={LOGO} alt="Uptimio" style={{ height: 42, marginBottom: 10 }} />
              {issuer && (
                <>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>
                    {issuer.displayName || issuer.issuer || issuer.companyName}
                  </div>
                  {addressLines(issuer).map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                  {issuer.phone && <div>{issuer.phone}</div>}
                  {issuer.email && <div>{issuer.email}</div>}
                  {issuer.taxId && <div>Tax ID: {issuer.taxId}</div>}
                </>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#6b7280', fontWeight: 700, marginBottom: 6 }}>SERVICE AGREEMENT</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{deal.name}</div>
              <div style={{ marginTop: 8, color: '#6b7280' }}>Date: {signedOn}</div>
              <div style={{ marginTop: 4 }}>{deal.kind === 'ONE_TIME' ? 'One-time' : 'Monthly recurring'}</div>
            </div>
          </div>

          <div style={{ marginTop: 28 }}>
            <PartyDetails party={client} title="Client" />
          </div>

          {deal.notes && (
            <div style={{ marginTop: 22 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>What is included</div>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: '#374151' }}>{deal.notes}</div>
            </div>
          )}

          <table className="deal-pdf-table">
            <tbody>
              <tr>
                <th>{deal.support ? 'Hours in package' : 'Quantity'}</th>
                <td>{deal.quantity}</td>
              </tr>
              <tr>
                <th>Unit price</th>
                <td>{money(deal.unitPrice)}</td>
              </tr>
              <tr>
                <th>Package amount</th>
                <td style={{ fontWeight: 700 }}>{money(dealAmount(deal))}</td>
              </tr>
              {deal.support && (
                <tr>
                  <th>Overage hourly rate</th>
                  <td>{money(deal.overageRate || 0)} / h after the package is used</td>
                </tr>
              )}
            </tbody>
          </table>

          <p style={{ marginTop: 22, color: '#6b7280', fontSize: 12, lineHeight: 1.5 }}>
            By signing below, both parties agree to the terms of this deal.
            {deal.support
              ? ' Ticket hours for the client company count against the package first. Time over the package is billed at the overage rate.'
              : ''}
          </p>

          <div className="deal-signatures">
            <div className="deal-signature">
              <div className="deal-signature-mark">
                <img src={ISSUER_SIGNATURE} alt="Issuer signature" />
              </div>
              <div className="deal-signature-line" />
              <span className="deal-signature-role">Issuer</span>
              <span className="deal-signature-name">{issuer?.displayName || issuer?.issuer || issuer?.signerName}</span>
            </div>
            <div className="deal-signature">
              <div className="deal-signature-mark">
                {deal.clientSignature ? (
                  <img src={getAssetUrl(deal.clientSignature)} alt="Client signature" />
                ) : null}
              </div>
              <div className="deal-signature-line" />
              <span className="deal-signature-role">Client</span>
              <span className="deal-signature-name">
                {client?.contactName || client?.companyName}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealPdf;
